// SYN-2721 — a página pública do relatório HTML (`/r/:token`).
//
// O token no path É a credencial: quem tem o link vê o relatório, sem login.
// Duas consequências moram aqui:
//
// 1. O boot do app não pode tratar essa aba como sessão. Com um token velho no
//    localStorage, o `/users/me` do store devolveria 401 e o logout jogaria o
//    visitante para a landing por cima do relatório. Por isso o teste é sobre o
//    `window.location`, e não sobre a rota: o store e o `App.vue` montam ANTES
//    de a navegação inicial resolver, quando a rota ainda é `START_LOCATION`.
// 2. Analytics nunca recebe o token. R1 admite ID em path, nunca credencial.
//
// en: the token in the path is the credential. The boot must not treat this tab
// as a session, and analytics must never receive the token.

export const PUBLIC_REPORT_ROUTE_NAME = 'PublicReport';

const PUBLIC_REPORT_PATH = /^\/r\/[^/]+\/?$/;

/** A aba atual é a página pública de um relatório? / Is this the public report page? */
export function isPublicReportPath(pathname = window.location.pathname) {
  return PUBLIC_REPORT_PATH.test(pathname);
}

// Path absoluto (`/r/x`) ou URL completa (`https://host/r/x?y`): só o segmento
// do token é trocado, o resto da URL fica como estava.
const TOKEN_SEGMENT = /^((?:[a-z][a-z0-9+.-]*:\/\/[^/?#]*)?\/r\/)[^/?#]+/i;

/** Troca o token de `/r/<token>` por `:token`. / Redacts the token of `/r/<token>`. */
export function redactPublicReportToken(url) {
  return url.replace(TOKEN_SEGMENT, '$1:token');
}

// O token também aparece fora do path da aba: na URL da API que a página consulta
// (`/public/reports/<token>`, em breadcrumbs de xhr e na tag `axios.url`) e no
// `request.url`/breadcrumbs de navegação do Sentry. Os dois formatos, em qualquer
// string do evento. O `/r/` só conta logo após o host, no início de um valor (a
// aspa da serialização) ou depois de espaço (texto de log) — nunca no meio de
// outro path.
const SENTRY_TOKEN_SEGMENTS = [
  /((?:"|\s|:\/\/[^/"\s]+)\/r\/)[A-Za-z0-9_-]+/g,
  /(\/public\/reports\/)[A-Za-z0-9_-]+/g,
];

// Terceiros que leem a URL da aba por conta própria, fora do nosso código: a tag
// da HubSpot manda `location.href` inteiro no beacon (`pu=`), e o Session Replay
// do Mixpanel grava a navegação. Nenhum dos dois aceita redação, então na página
// pública eles simplesmente não ligam. A tag saiu do index.html para cá porque o
// HTML estático não sabe em que rota está.
// en: the HubSpot tag and Mixpanel's Session Replay read the tab's URL on their
// own and cannot be redacted, so neither runs on the public page.
export const HUBSPOT_SCRIPT_SRC = '//js-na1.hs-scripts.com/51265689.js';

/** Injeta a tag da HubSpot, exceto na página pública. / Loads HubSpot, except on /r/. */
export function loadHubSpot(doc = document, pathname = window.location.pathname) {
  if (isPublicReportPath(pathname)) return false;
  const script = doc.createElement('script');
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = HUBSPOT_SCRIPT_SRC;
  doc.body.appendChild(script);
  return true;
}

/** Percentual do Session Replay: zero na página pública. / Replay off on /r/. */
export function replayPercentFor(percent, pathname = window.location.pathname) {
  return isPublicReportPath(pathname) ? 0 : percent;
}

/**
 * Hook de `beforeSend` do Sentry: redige o token público em todo o evento.
 * O evento chega normalizado (JSON-safe) ao `beforeSend`, então a troca é feita
 * sobre a serialização — cobre URL, breadcrumbs, tags, contexto e fingerprint.
 * en: redacts the public report token anywhere in a Sentry event.
 */
export function redactSentryEvent(event) {
  const raw = JSON.stringify(event);
  const redacted = SENTRY_TOKEN_SEGMENTS.reduce((text, re) => text.replace(re, '$1:token'), raw);
  return redacted === raw ? event : JSON.parse(redacted);
}
